import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Building2, ChevronDown, Check } from 'lucide-react';

/**
 * Lightweight multi-select branch filter (shadcn Popover + Checkbox).
 *
 *   value:    array of branch ids ([] = all branches)
 *   onChange: fn(array)
 *   branches: [{ id, name }]
 */
export function BranchMultiSelect({ value = [], onChange, branches = [], width = 'w-56', testid = 'branch-multiselect' }) {
  const selected = Array.isArray(value) ? value : [];
  const [q, setQ] = useState('');
  const shown = q ? branches.filter((b) => (b.name || '').toLowerCase().includes(q.toLowerCase())) : branches;

  const label = useMemo(() => {
    if (!selected.length) return 'All Branches';
    if (selected.length === 1) return branches.find((b) => b.id === selected[0])?.name || '1 branch';
    return `${selected.length} branches`;
  }, [selected, branches]);

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={`${width} h-10 rounded-xl justify-between font-normal`} data-testid={`${testid}-trigger`}>
          <span className="flex items-center gap-2 truncate">
            <Building2 size={14} className="shrink-0 text-muted-foreground" />
            <span className="truncate">{label}</span>
          </span>
          <ChevronDown size={14} className="shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 rounded-xl" align="start" data-testid={`${testid}-content`}>
        <button
          type="button"
          onClick={() => onChange([])}
          className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-sm hover:bg-muted transition-colors"
          data-testid={`${testid}-all`}
        >
          <span className="font-medium">All Branches</span>
          {selected.length === 0 && <Check size={14} className="text-primary" />}
        </button>
        <div className="h-px bg-border my-1" />
        {branches.length > 6 && (
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search branches…" className="w-full mb-1 px-2 py-1 text-xs rounded-md border border-border bg-background" data-testid={`${testid}-search`} />
        )}
        <div className="max-h-[60vh] overflow-y-auto pr-1" data-testid={`${testid}-scroll`}>
          <div className="space-y-0.5">
            {shown.map((b) => (
              <label
                key={b.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm hover:bg-muted cursor-pointer transition-colors"
                data-testid={`${testid}-option-${b.id}`}
              >
                <Checkbox checked={selected.includes(b.id)} onCheckedChange={() => toggle(b.id)} />
                <span className="truncate">{b.name}</span>
              </label>
            ))}
            {branches.length === 0 && <p className="px-2 py-2 text-xs text-muted-foreground">No branches</p>}
          </div>
        </div>
        {selected.length > 0 && (
          <>
            <div className="h-px bg-border my-1" />
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full text-center px-2 py-1 rounded-lg text-xs text-muted-foreground hover:bg-muted transition-colors"
              data-testid={`${testid}-clear`}
            >
              Clear selection
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default BranchMultiSelect;
